import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ZoomIn, ZoomOut, Pencil } from "lucide-react";
import { useState } from "react";
import { EditReceiptDialog } from "./edit-receipt-dialog";
import { Receipt } from "@/types/expense";
import { base64ToDataUrl } from "@/utils/image";

interface ReceiptImageViewerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageData: string;
  receiptName: string;
  receipt?: Receipt;
  onUpdate?: (receipt: Receipt) => void;
}

export function ReceiptImageViewer({
  open,
  onOpenChange,
  imageData,
  receiptName,
  receipt,
  onUpdate,
}: ReceiptImageViewerProps) {
  const [zoom, setZoom] = useState(100);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 25, 50));
  };

  const handleClose = () => {
    setZoom(100);
    onOpenChange(false);
  };

  const handleEditClick = () => {
    setEditDialogOpen(true);
  };

  const handleEditSave = (updatedReceipt: Receipt) => {
    console.log("Receipt updated:", updatedReceipt);
    // Call the onUpdate callback to save to backend
    if (onUpdate) {
      onUpdate(updatedReceipt);
    }
    setEditDialogOpen(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] h-[95vh] p-0 gap-0">
          {/* Header with controls */}
          <div className="flex items-center justify-between p-4 pt-8 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex-1">
              <h2 className="text-lg font-semibold">{receiptName}</h2>
              <p className="text-sm text-muted-foreground">
                Click and drag to pan • Scroll to zoom
              </p>
            </div>

            <div className="flex items-center gap-2">
              {/* Zoom controls */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomOut}
                disabled={zoom <= 50}
                className="w-20"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium min-w-[3rem] text-center">
                {zoom}%
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomIn}
                disabled={zoom >= 200}
                className="w-20"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>

              {/* Edit button - only show for unprocessed receipts */}
              {receipt && !receipt.processed && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEditClick}
                  className="gap-2 w-20 ml-2"
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </Button>
              )}
            </div>
          </div>

          {/* Image container */}
          <div className="flex-1 overflow-auto bg-muted/20 flex items-center justify-center p-4">
            <div
              className="transition-transform duration-200 ease-out"
              style={{
                transform: `scale(${zoom / 100})`,
                transformOrigin: "center center",
              }}
            >
              <img
                src={base64ToDataUrl(imageData)}
                alt={receiptName}
                className="max-w-full h-auto shadow-2xl rounded-lg"
                style={{
                  maxHeight: "calc(95vh - 120px)",
                }}
              />
            </div>
          </div>

          {/* Footer with keyboard shortcuts */}
          <div className="flex items-center justify-center gap-4 p-2 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 text-xs text-muted-foreground">
            <span>
              <kbd className="px-1.5 py-0.5 bg-muted rounded">Esc</kbd> Close
            </span>
            <span>
              <kbd className="px-1.5 py-0.5 bg-muted rounded">+</kbd> Zoom In
            </span>
            <span>
              <kbd className="px-1.5 py-0.5 bg-muted rounded">-</kbd> Zoom Out
            </span>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      {receipt && (
        <EditReceiptDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          receipt={receipt}
          onSave={handleEditSave}
        />
      )}
    </>
  );
}
